import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getUserProfileAction, logoutAction } from "../../../redux/slices/users/usersSlice";
import CustomerDetails from "./CustomerDetails";
import Footer from "../../Footer/Footer";
import { Bars } from "react-loader-spinner";
import GLOBALS from "../../../utils/Globals";
import axios from "axios";
import ChangePassword from "./ChangePassword";
import { FaKey } from 'react-icons/fa';
import Form from "react-bootstrap/Form";

export default function CustomerProfile() {
  const baseURLAddPhotoToS3 =
    process.env.REACT_APP_ENV === "PROD"
      ? GLOBALS.PROD.baseURLAddPhotoToS3
      : GLOBALS.DEV.baseURLAddPhotoToS3;

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  
  // Initialize dashboard preference state from localStorage
  const [showDashboard, setShowDashboard] = useState(() => {
    const savedPreference = localStorage.getItem('showDashboard');
    return savedPreference === null ? true : JSON.parse(savedPreference);
  });

  // Initialize quick drop preference state from localStorage
  const [showQuickDrop, setShowQuickDrop] = useState(() => {
    const savedPreference = localStorage.getItem('showQuickDrop');
    return savedPreference === null ? true : JSON.parse(savedPreference);
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get state from Redux store
  const { error, loading, profile } = useSelector((state) => state?.users);

  useEffect(() => {
    dispatch(getUserProfileAction());
  }, [dispatch]);

  // Save dashboard preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('showDashboard', JSON.stringify(showDashboard));
  }, [showDashboard]);

  // Save quick drop preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('showQuickDrop', JSON.stringify(showQuickDrop));
  }, [showQuickDrop]);

  // Handle authentication errors
  useEffect(() => {
    if (error && (
      error?.message?.includes("session has expired") ||
      error?.message?.includes("authentication") ||
      error?.message?.includes("login again")
    )) {
      // Logout and redirect to login
      dispatch(logoutAction());
      navigate('/login');
    }
  }, [error, dispatch, navigate]);

  // Helper function to safely format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : date.toDateString();
  };

  // Helper function to safely get year
  const getYear = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : date.getFullYear().toString();
  };
  
  // Handle dashboard toggle
  const handleToggleDashboard = (e) => {
    setShowDashboard(e.target.checked);
  };

  // Handle quick drop toggle
  const handleToggleQuickDrop = (e) => {
    const newValue = e.target.checked;
    setShowQuickDrop(newValue);
    localStorage.setItem('showQuickDrop', JSON.stringify(newValue));
    // Clear dismissal state when re-enabling
    if (newValue) {
      localStorage.removeItem('quickDropDismissed');
    }
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('quickDropToggle'));
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file");
      return;
    }
    if (file.size > 5000000) { // 5MB limit
      alert("File size too large. Please upload a file smaller than 5MB");
      return;
    }

    setAvatarFile(file);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("email", profile?.user?.email || "");
      // Add the target folder path
      formData.append("folder_path", `${profile?.user?.email}/DOC_FOLDER/images`);

      const response = await axios.post(baseURLAddPhotoToS3, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setAvatarUrl(response.data.url);
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert("Failed to upload avatar");
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Bars
            height="80"
            width="80"
            color="#3b82f6"
            ariaLabel="loading"
            wrapperClass=""
            visible={true}
          />
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-md">
          <div className="text-red-500 text-xl mb-4">Error loading profile</div>
          <p className="text-gray-600">
            {typeof error === 'string' ? error : error?.message || 'An error occurred while loading your profile'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="p-6">
            <div className="flex justify-between mb-4">
              <div className="flex flex-col space-y-2">
                <Form.Check
                  type="checkbox"
                  id="dashboard-toggle"
                  label="Show Dashboard on Homepage"
                  checked={showDashboard}
                  onChange={handleToggleDashboard}
                  className="text-sm font-medium text-gray-600"
                />
                <Form.Check
                  type="checkbox"
                  id="quickdrop-toggle"
                  label="Show Quick Drag and Drop on Homepage"
                  checked={showQuickDrop}
                  onChange={handleToggleQuickDrop}
                  className="text-sm font-medium text-gray-600"
                />
              </div>
              <button
                onClick={() => setIsChangePasswordOpen(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <FaKey className="w-4 h-4 mr-2" />
                Change Password
              </button>
            </div>
            <CustomerDetails
              email={profile?.user?.email || ""}
              dateJoined={formatDate(profile?.user?.createdAt)}
              fullName={profile?.user?.fullname || ""}
              org={profile?.user?.org || ""}
              account_id={profile?.user?.account_id || ""}
              isAdmin={!!profile?.user?.isAdmin}
              avatarUrl={avatarUrl}
              onAvatarChange={handleAvatarChange}
              isUploading={isUploading}
            />
          </div>
        </div>

        {/* Additional Information or Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Member Since
            </h3>
            <p className="text-3xl font-bold text-blue-600">
              {getYear(profile?.user?.createdAt)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Account Status
            </h3>
            <p className="text-3xl font-bold text-green-600">Active</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Role</h3>
            <p className="text-3xl font-bold text-purple-600">
              {profile?.user?.isAdmin ? "Admin" : "Customer"}
            </p>
          </div>
        </div>
        {isChangePasswordOpen && (
          <ChangePassword onClose={() => setIsChangePasswordOpen(false)} />
        )}
      </div>
      <Footer />
    </div>
  );
}
